import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

const initialState = {
  projects: [],
  starredProjects: [],
  currentProject: null,
  members: [],
  stats: null,
  pagination: {
    total: 0,
    page: 1,
    pageSize: 20,
    totalPages: 1,
  },
  loading: false,
  error: null,
};

// Async thunks
export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async ({ page = 1, pageSize = 20, search = '', status = '' } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);
      if (search) params.append('search', search);
      if (status) params.append('status', status);

      const response = await api.get(`/projects?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch projects'
      );
    }
  }
);

export const fetchProjectById = createAsyncThunk(
  'projects/fetchProjectById',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/projects/${projectId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch project'
      );
    }
  }
);

export const createProject = createAsyncThunk(
  'projects/createProject',
  async (projectData, { rejectWithValue }) => {
    try {
      const response = await api.post('/projects', projectData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to create project'
      );
    }
  }
);

export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async ({ projectId, projectData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/projects/${projectId}`, projectData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to update project'
      );
    }
  }
);

export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (projectId, { rejectWithValue }) => {
    try {
      await api.delete(`/projects/${projectId}`);
      return projectId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to delete project'
      );
    }
  }
);

export const archiveProject = createAsyncThunk(
  'projects/archiveProject',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.post(`/projects/${projectId}/archive`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to archive project'
      );
    }
  }
);

export const fetchStarredProjects = createAsyncThunk(
  'projects/fetchStarredProjects',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/projects/starred');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch starred projects');
    }
  }
);

export const toggleProjectStar = createAsyncThunk(
  'projects/toggleProjectStar',
  async ({ projectId, isCurrentlyStarred }, { rejectWithValue }) => {
    try {
      const response = isCurrentlyStarred
        ? await api.delete(`/projects/${projectId}/star`)
        : await api.post(`/projects/${projectId}/star`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update star');
    }
  }
);

export const fetchProjectStats = createAsyncThunk(
  'projects/fetchProjectStats',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/projects/${projectId}/stats`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch stats'
      );
    }
  }
);

// Project Members
export const fetchProjectMembers = createAsyncThunk(
  'projects/fetchProjectMembers',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/projects/${projectId}/members`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch members'
      );
    }
  }
);

export const addProjectMember = createAsyncThunk(
  'projects/addProjectMember',
  async ({ projectId, memberData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/projects/${projectId}/members`, memberData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to add member'
      );
    }
  }
);

export const updateProjectMemberRole = createAsyncThunk(
  'projects/updateProjectMemberRole',
  async ({ projectId, userId, role }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/projects/${projectId}/members/${userId}`, { role });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to update member role'
      );
    }
  }
);

export const removeProjectMember = createAsyncThunk(
  'projects/removeProjectMember',
  async ({ projectId, userId }, { rejectWithValue }) => {
    try {
      await api.delete(`/projects/${projectId}/members/${userId}`);
      return userId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to remove member'
      );
    }
  }
);

const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    clearCurrentProject: (state) => {
      state.currentProject = null;
      state.members = [];
      state.stats = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch projects
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload.projects;
        state.pagination = {
          total: action.payload.total,
          page: action.payload.page,
          pageSize: action.payload.page_size,
          totalPages: action.payload.total_pages,
        };
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch project by ID
      .addCase(fetchProjectById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProject = action.payload;
      })
      .addCase(fetchProjectById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create project
      .addCase(createProject.fulfilled, (state, action) => {
        state.projects.unshift(action.payload);
        state.pagination.total += 1;
      })
      // Update project
      .addCase(updateProject.fulfilled, (state, action) => {
        const index = state.projects.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.projects[index] = action.payload;
        }
        if (state.currentProject?.id === action.payload.id) {
          state.currentProject = action.payload;
        }
      })
      // Delete project
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.projects = state.projects.filter(p => p.id !== action.payload);
        state.pagination.total -= 1;
      })
      // Archive project
      .addCase(archiveProject.fulfilled, (state, action) => {
        const index = state.projects.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.projects[index] = action.payload;
        }
        if (state.currentProject?.id === action.payload.id) {
          state.currentProject = action.payload;
        }
      })
      // Fetch stats
      .addCase(fetchProjectStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      // Fetch members
      .addCase(fetchProjectMembers.fulfilled, (state, action) => {
        state.members = action.payload;
      })
      // Add member
      .addCase(addProjectMember.fulfilled, (state, action) => {
        state.members.push(action.payload);
      })
      // Update member role
      .addCase(updateProjectMemberRole.fulfilled, (state, action) => {
        const index = state.members.findIndex(m => m.id === action.payload.id);
        if (index !== -1) {
          state.members[index] = action.payload;
        }
      })
      // Remove member
      .addCase(removeProjectMember.fulfilled, (state, action) => {
        state.members = state.members.filter(m => m.user.id !== action.payload);
      })
      // Starred projects
      .addCase(fetchStarredProjects.fulfilled, (state, action) => {
        state.starredProjects = action.payload;
      })
      .addCase(toggleProjectStar.fulfilled, (state, action) => {
        const updated = action.payload;
        const idx = state.projects.findIndex(p => p.id === updated.id);
        if (idx !== -1) state.projects[idx] = updated;
        if (updated.is_starred) {
          if (!state.starredProjects.find(p => p.id === updated.id)) {
            state.starredProjects.push(updated);
          }
        } else {
          state.starredProjects = state.starredProjects.filter(p => p.id !== updated.id);
        }
      });
  },
});

export const { clearCurrentProject, clearError } = projectsSlice.actions;
export default projectsSlice.reducer;
